import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// components
import ChatBubble from 'eCoach/ChatBubble';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
/* eslint-disable import/no-duplicates */
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Alert from '@mui/material/Alert';

// styles
import * as Buttons from 'theme/components/Buttons.style';
import * as StyledPromptControls from 'eCoach/_styles/PromptControls.style';
import * as Text from 'theme/components/Text.style';

interface WarningLetterDateInputProps {
  sendUserInput: (text: string, type: number) => void;
  inputType: number;
  steps?: Array<any>;
}

export default function WarningLetterDateInput({
  sendUserInput,
  inputType,
  steps = [],
}: WarningLetterDateInputProps): JSX.Element {
  const { t, i18n } = useTranslation('pageEcoach');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [requiredDates, setRequiredDates] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Get current locale
  const locale = i18n.language === 'fr' ? fr : undefined;

  // Extract required number of dates from the last message
  useEffect(() => {
    if (steps && steps.length > 0) {
      const lastStep = steps[steps.length - 1];
      if (lastStep?.outputs && lastStep.outputs.length > 0) {
        const lastOutput = lastStep.outputs[lastStep.outputs.length - 1];
        if (lastOutput?.message) {
          // English pattern: "Please provide the dates for each of the X absences"
          // French pattern would be similar with "Veuillez fournir les dates pour chacune des X absences"
          const englishMatch = lastOutput.message.match(
            /Please provide the dates for each of the (\d+) absences/
          );
          const frenchMatch = lastOutput.message.match(
            /Veuillez fournir les dates pour chacune des (\d+) absences/
          );

          if (englishMatch && englishMatch[1]) {
            setRequiredDates(parseInt(englishMatch[1], 10));
          } else if (frenchMatch && frenchMatch[1]) {
            setRequiredDates(parseInt(frenchMatch[1], 10));
          }
        }
      }
    }
  }, [steps]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setErrorMessage('');
  };

  const handleAddDate = () => {
    if (selectedDates.length >= requiredDates) {
      setErrorMessage(
        t('You cannot add more dates. {{requiredDates}} dates are required.', {
          requiredDates,
        })
      );
      return;
    }

    if (
      selectedDate &&
      !selectedDates.some(
        (d) =>
          d.getDate() === selectedDate.getDate() &&
          d.getMonth() === selectedDate.getMonth() &&
          d.getFullYear() === selectedDate.getFullYear()
      )
    ) {
      setSelectedDates([...selectedDates, selectedDate]);
      setSelectedDate(null);
      setErrorMessage('');
    }
  };

  const handleDeleteDate = (dateToDelete: Date) => {
    setSelectedDates(
      selectedDates.filter(
        (date) =>
          !(
            date.getDate() === dateToDelete.getDate() &&
            date.getMonth() === dateToDelete.getMonth() &&
            date.getFullYear() === dateToDelete.getFullYear()
          )
      )
    );
    setErrorMessage('');
  };

  const handleSubmit = useCallback(() => {
    if (selectedDates.length !== requiredDates) {
      setErrorMessage(
        t(
          'Please provide exactly {{requiredDates}} dates. Currently you have {{currentDates}}.',
          {
            requiredDates,
            currentDates: selectedDates.length,
          }
        )
      );
      return;
    }

    // Format dates based on current locale
    const formattedDates = selectedDates
      .map((date) =>
        i18n.language === 'fr'
          ? format(date, 'd MMM yyyy', { locale: fr })
          : format(date, 'MMM d yyyy')
      )
      .join(', ');

    sendUserInput(formattedDates, inputType);
  }, [
    selectedDates,
    requiredDates,
    inputType,
    sendUserInput,
    t,
    i18n.language,
  ]);

  return (
    <ChatBubble sentByUser>
      <StyledPromptControls.InputGroup>
        <Text.P2>
          {t('Please enter {{requiredDates}} date(s) for the absences', {
            requiredDates,
          })}
        </Text.P2>

        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={locale}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{ textField: { fullWidth: true } }}
              maxDate={new Date()}
            />
            <Buttons.SecondaryDark
              onClick={handleAddDate}
              inactive={!selectedDate || selectedDates.length >= requiredDates}
              small
            >
              {t('Add Date')}
            </Buttons.SecondaryDark>
          </Box>
        </LocalizationProvider>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ mb: 1 }}>
          <Text.P2>
            {t('Selected: {{current}}/{{required}}', {
              current: selectedDates.length,
              required: requiredDates,
            })}
          </Text.P2>
        </Box>

        {selectedDates.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {selectedDates.map((date, index) => (
                <Chip
                  key={index}
                  label={
                    i18n.language === 'fr'
                      ? format(date, 'd MMM yyyy', { locale: fr })
                      : format(date, 'MMM d, yyyy')
                  }
                  onDelete={() => handleDeleteDate(date)}
                  sx={{ margin: 0.5 }}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Buttons.SecondaryDark
          onClick={handleSubmit}
          inactive={selectedDates.length !== requiredDates}
          small
          stretch
        >
          {t('Submit')}
        </Buttons.SecondaryDark>
      </StyledPromptControls.InputGroup>
    </ChatBubble>
  );
}
